import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { type PlayerViewModelData, type TitleOpts, type ViewedStream } from '../types';

/**
 * App header Redux state
 *
 * This contains only the mutable state that can be changed via actions.
 * Computed/derived state (layout, hideMobileMenu, activeTab) and external data
 * (brand config, current user, navigation helpers) are not stored here.
 */
// oxlint-disable-next-line quantic/no-type-exports
export type AppHeaderReduxState = {
    /** Whether the header is hidden */
    hidden: boolean;

    /** Whether there's a top message to display */
    hasTopMessage: boolean;

    /** Whether to show the caret indicator */
    showCaret: boolean;

    /** Whether to show the alternate home button (for inner screens) */
    showAlternateHomeButton: boolean;

    /** The title HTML to display */
    title: string | undefined;

    /** Title display options */
    titleOpts: TitleOpts;

    /** Currently viewed stream (for bookmarking/sharing on mobile) */
    viewedStream: ViewedStream | null;

    /** Whether the mobile keyboard is showing */
    mobileKeyboardShowing: boolean;

    /** Player view model data for lesson-player layout */
    playerViewModel: PlayerViewModelData | null;
};

const initialState: AppHeaderReduxState = {
    hidden: false,
    hasTopMessage: false,
    showCaret: false,
    showAlternateHomeButton: false,
    title: undefined,
    titleOpts: {},
    viewedStream: null,
    mobileKeyboardShowing: false,
    playerViewModel: null,
};

const appHeaderSliceInternal = createSlice({
    name: 'appHeader',
    initialState,
    reducers: {
        /**
         * Set header visibility
         */
        setHidden(state, action: PayloadAction<boolean>) {
            state.hidden = action.payload;
        },

        /**
         * Toggle header visibility (convenience action)
         */
        toggleVisibility(state, action: PayloadAction<boolean>) {
            state.hidden = !action.payload;
        },

        /**
         * Set whether there's a top message
         */
        setHasTopMessage(state, action: PayloadAction<boolean>) {
            state.hasTopMessage = action.payload;
        },

        /**
         * Set whether to show the caret indicator
         */
        setShowCaret(state, action: PayloadAction<boolean>) {
            state.showCaret = action.payload;
        },

        /**
         * Set whether to show the alternate home button
         */
        setShowAlternateHomeButton(state, action: PayloadAction<boolean>) {
            state.showAlternateHomeButton = action.payload;
        },

        /**
         * Set the title HTML
         */
        setTitle(state, action: PayloadAction<string | undefined>) {
            state.title = action.payload;
        },

        /**
         * Set the title options
         */
        setTitleOpts(state, action: PayloadAction<TitleOpts>) {
            state.titleOpts = action.payload;
        },

        /**
         * Set both title and title options at once
         */
        setTitleHTML(state, action: PayloadAction<{ title?: string; opts?: TitleOpts }>) {
            state.title = action.payload.title;
            state.titleOpts = action.payload.opts ?? {};
        },

        /**
         * Set the currently viewed stream
         */
        setViewedStream(state, action: PayloadAction<ViewedStream | null>) {
            state.viewedStream = action.payload;
        },

        /**
         * Update the favorite status of the viewed stream
         */
        updateViewedStreamFavorite(state, action: PayloadAction<boolean>) {
            if (state.viewedStream) {
                state.viewedStream.favorite = action.payload;
            }
        },

        /**
         * Set whether the mobile keyboard is showing
         */
        setMobileKeyboardShowing(state, action: PayloadAction<boolean>) {
            state.mobileKeyboardShowing = action.payload;
        },

        /**
         * Set the player view model data for lesson-player layout
         */
        setPlayerViewModel(state, action: PayloadAction<PlayerViewModelData | null>) {
            state.playerViewModel = action.payload;
        },

        /**
         * Update specific player view model properties
         */
        updatePlayerViewModel(state, action: PayloadAction<Partial<PlayerViewModelData>>) {
            if (state.playerViewModel) {
                state.playerViewModel = { ...state.playerViewModel, ...action.payload };
            }
        },

        /**
         * Reset state to initial values
         */
        resetState() {
            return initialState;
        },
    },
});

// Export the slice
export const appHeaderSlice = appHeaderSliceInternal;

// Export actions
export const appHeaderActions = appHeaderSliceInternal.actions;

// Type for state that includes appHeader
type StateWithAppHeader = { appHeader?: AppHeaderReduxState };

// Selectors
export const selectAppHeader = (state: StateWithAppHeader): AppHeaderReduxState => state.appHeader ?? initialState;

export const selectAppHeaderHidden = (state: StateWithAppHeader): boolean => selectAppHeader(state).hidden;

export const selectAppHeaderHasTopMessage = (state: StateWithAppHeader): boolean =>
    selectAppHeader(state).hasTopMessage;

export const selectAppHeaderShowCaret = (state: StateWithAppHeader): boolean => selectAppHeader(state).showCaret;

export const selectAppHeaderShowAlternateHomeButton = (state: StateWithAppHeader): boolean =>
    selectAppHeader(state).showAlternateHomeButton;

export const selectAppHeaderTitle = (state: StateWithAppHeader): string | undefined => selectAppHeader(state).title;

export const selectAppHeaderTitleOpts = (state: StateWithAppHeader): TitleOpts => selectAppHeader(state).titleOpts;

export const selectAppHeaderViewedStream = (state: StateWithAppHeader): ViewedStream | null =>
    selectAppHeader(state).viewedStream;

export const selectAppHeaderMobileKeyboardShowing = (state: StateWithAppHeader): boolean =>
    selectAppHeader(state).mobileKeyboardShowing;

export const selectAppHeaderPlayerViewModel = (state: StateWithAppHeader): PlayerViewModelData | null =>
    selectAppHeader(state).playerViewModel;
